import * as Actions from '../actions/taskActions';
import Task from '../../entities/task';
import * as idb from '../../lib/idbd';
import {readBaseVersion, versionRef} from '../../lib/staleSave';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {getSessionFirmId, getSessionUserId} from '../../lib/picos/sessionIdentity';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type TaskAction = {
    type: string;
    task: Task | null; 
    taskId: string | Task; 
    partial: boolean; 
    lastFetch: number; 
    tasks: Task[]; 
    err: any; 
    status: number; 
    message: string; 
    rejectedAddedOn?: number;
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Task> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Task[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Task> | null, firmId: string, userId: string, updates?: Task[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'task', 'tasks', data ? backupRows(data) : null, updates)
		  .then(result => {
			  // OWA-786 (CXR-4-001): the worker resolves a typed result for every
			  // path — a resolution is NOT an acknowledgement. Only ok: true means
			  // the backup write is durable; anything else is surfaced, never
			  // swallowed, so a skipped or failed write cannot masquerade as one.
			  if (!result?.ok) console.warn(`⮂ taskReducer: backup write for 'tasks' did not persist (${result?.reason ?? 'unknown'}): ${result?.message ?? ''}`);
		  })
		  .catch(console.error);
}

/**
 * The identity that names this tab's backup store, or null when this tab has
 * not finished authenticating.
 *
 * OWA-785: this must come from sessionStorage (per tab), never from
 * localStorage (shared by every tab on the origin). With two firms open in two
 * tabs, the localStorage value is whichever firm logged in last, so the other
 * tab persisted its entities into that firm's backup store and they restored
 * into the wrong firm's UI. A partial identity names some other store, so we
 * skip the write rather than misfile it.
 *
 * @returns {{firmId: string, userId: string} | null}
 */
function storageIdentity(): {firmId: string; userId: string} | null {
	const firmId = getSessionFirmId();
	const userId = getSessionUserId();

	if (!firmId || !userId) {
		if (logging) console.debug('⮂ taskReducer: no session identity, skipping backup write');
		return null;
	}

	return {firmId, userId};
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Task>} data
 * @param {Task[]} [updates]
 * @returns {ArrayMap<Task>}
 */
function updateStorage(data: ArrayMap<Task>, updates?: Task[]): ArrayMap<Task> {
	if (logging) console.debug('⮂ taskReducer: updateStorage');

	if (updates && updates.length === 0) return data;

	const identity = storageIdentity();
	if (!identity) return data;

	if (updates) callStorageWorker(null, identity.firmId, identity.userId, updates);
	else callStorageWorker(data, identity.firmId, identity.userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Task[]} [updates]
 * @returns {ArrayMap<Task>}
 */
function updateStorageNoReturn(updates?: Task[]): void {
	if (logging) console.debug('⮂ taskReducer: updateStorageNoReturn');

	if (!updates || updates.length === 0) return;

	const identity = storageIdentity();
	if (!identity) return;

	callStorageWorker(null, identity.firmId, identity.userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Task[]} [data]
 * @returns {ArrayMap<Task>}
 */
function createState(data?: Task[] | ArrayMap<Task>): ArrayMap<Task> {
	if (!data) return ArrayMapRegistry.clone<Task>('task', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Task>('task', data.map(e => e));
    else return ArrayMapRegistry.clone<Task>('task', data);
}

// Whether deleted tasks keep their rows (schema includeDeletedInResults). OWA-68
const retainDeleted = false;

/**
 * The full backup snapshot of a collection: one entry per ID. A row when there is one (deleted rows of a
 * type that keeps them included); otherwise a deletion record for an ID whose row was removed. OWA-68
 *
 * @param {ArrayMap<Task>} data
 * @returns {Array<Task | TaskDeletionRecord>}
 */
function backupRows(data: ArrayMap<Task>): Array<Task | TaskDeletionRecord> {
	const records: TaskDeletionRecord[] = data.deletedVersionEntries()
		.filter(([id]) => !data.has(id))
		.map(([id, addedOn]) => ({taskId: id, addedOn, status: 'D', deletionRecord: true}));

	return records.length ? [...data.values(), ...records] : data.values();
}

/**
 * A deletion record read back from the offline backup: evidence that a task was deleted at a
 * version, never a live row. The storage worker writes one for a deleted task whose type does
 * not keep deleted rows. OWA-68
 */
type TaskDeletionRecord = {taskId: string; addedOn: number; status: 'D'; deletionRecord: true};

const isDeletionRecord = (o: unknown): o is TaskDeletionRecord => (o as TaskDeletionRecord)?.deletionRecord === true;

/**
 * OWA-998: a version that came from the server (no pending save of this tab, no recorded base) is an
 * accepted version, so an edit cloned from it names it as its base.
 */
function accepted(incoming: Task): Task {
	if (incoming.pendingOp || readBaseVersion(incoming.baseVersion).kind !== 'absent' || !incoming.dbAddedOn) return incoming;
	return new Task({...incoming, baseVersion: versionRef(incoming as Task & {addedOn: number})});
}

/** OWA-998: an accepted version older than `than`, by database version, then by addedOn. */
function olderAccepted(row: Task, than: Task): boolean {
	if (row.pendingOp) return false;
	const rowDb = Number(row.dbAddedOn ?? 0), thanDb = Number(than.dbAddedOn ?? 0);
	return rowDb < thanDb || (rowDb === thanDb && row.addedOn < than.addedOn);
}

/**
 * Apply one incoming version of a task to a collection the caller owns (a clone), under the one
 * acceptance rule every path uses (OWA-68):
 * - older than the stored row: rejected
 * - an active version equal to a kept deleted row: rejected (it yields only to a strictly newer one)
 * - no stored row, and at or below the recorded deletion version: rejected
 * - an accepted deleted version records its deletion version; the row is kept as a deleted row when
 *   this type keeps deleted rows, otherwise it is removed
 * - an accepted active version is stored (a newer one supersedes the deletion version)
 *
 * @param {ArrayMap<Task>} st
 * @param {Task | TaskDeletionRecord} incoming
 * @returns {boolean} whether the version was accepted; only accepted versions go to the backup
 */
function applyVersion(st: ArrayMap<Task>, incoming: Task | TaskDeletionRecord): boolean {
	const id = isDeletionRecord(incoming) ? incoming.taskId : incoming.id;
	const current = st.get(id);

	if (current) {
		if (incoming.addedOn < current.addedOn) return false;
		// A kept deleted row yields only to a strictly newer active version, like a deletion version
		if (current.status === 'D' && incoming.status !== 'D' && incoming.addedOn === current.addedOn) return false;
	}
	else {
		const deletedAt = st.deletedVersion(id);
		if (deletedAt !== undefined && incoming.addedOn <= deletedAt) return false;
	}

	if (incoming.status === 'D') {
		st.recordDeletion(id, incoming.addedOn);
		if (retainDeleted && !isDeletionRecord(incoming)) st.set(incoming);
		else if (current) st.delete(current);
	}
	else st.set(accepted(incoming as Task));

	return true;
}

/**
 * A new indexed collection holding the rows and deletion versions of the given one. Fetch paths build
 * from this instead of cloning, because the initial store state is a bare ArrayMap without the
 * schema's indexes. OWA-68
 *
 * @param {ArrayMap<Task>} from
 * @returns {ArrayMap<Task>}
 */
function carryOver(from: ArrayMap<Task>): ArrayMap<Task> {
	const next = createState(from);
	from.deletedVersionEntries().forEach(([id, version]) => next.recordDeletion(id, version));
	return next;
}

function tasks(state: ArrayMap<Task>, action: TaskAction): ArrayMap<Task> {
	if (!state) state = createState();

	let st: ArrayMap<Task>;

	switch (action.type) {
		// action.task - the new value of task with id action.id
		// action.taskId - id of the target, used if new value is null
		case Actions.TASK_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ taskReducer: FETCH_ONE_SUCCESS', action);

			if (action.task === null) return state;

			// Only for a task this collection knows (a row, or a deletion version), and only when the
			// fetched version is accepted (OWA-68).
			if (state.has(action.taskId) || state.deletedVersion(action.task.id) !== undefined) {
				st = state.clone();
				if (!applyVersion(st, action.task)) return state;

				updateStorageNoReturn([action.task]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.tasks
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.tasks
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.tasks
			 !partial, !stateEmpty, fetchEmpty	-> D return tasks in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge tasks in state newer than those in action.tasks
		 */
		case Actions.TASK_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.tasks.length === 0;
			// A deletion record from the backup is evidence only: never constructed into an entity (OWA-68)
			const fetched: Array<Task | TaskDeletionRecord> = action.tasks.map(task => isDeletionRecord(task) ? task : new Task({...task, persisted: true}));
			const fetchedIds = new Set(fetched.map(task => isDeletionRecord(task) ? task.taskId : task.id));

			// A full fetch is the whole dataset, so a persisted active row it omits is dropped (deleted rows,
			// deletion versions and unsaved local rows stay). OWA-68
			const dropOmitted = (st: ArrayMap<Task>) => {
				[...st.values()].forEach(row => {
					if (!fetchedIds.has(row.id) && row.persisted && row.status !== 'D') st.delete(row);
				});
			};

			if (logging) console.debug(`⮂ taskReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} tasks with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ taskReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Build a new indexed state that keeps any deletion versions (the visible state is
					// empty, but a deletion version can still reject an older row). OWA-68
					st = carryOver(state);
					fetched.forEach(task => applyVersion(st, task));

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ taskReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Task> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Task[] = [];

				// Partials include deleted rows for every entity; applyVersion keeps or removes them and
				// records their deletion version (OWA-68). Only accepted versions go to the backup.
				fetched.forEach(task => {
					if (applyVersion(st, task)) toUpdate.push(task as Task);
					else if (logging) console.debug('⮂ taskReducer: partialMerge: rejected older version of task');
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ taskReducer: empty: tasks', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Drop the persisted active rows; deleted rows, deletion versions and unsaved local rows
				// stay (OWA-68)
				st = carryOver(state);
				dropOmitted(st);

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ taskReducer: fullMerge: tasks', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Start from the current rows and deletion versions, apply each fetched version under the one
				// rule, then drop persisted active rows the full fetch omitted (OWA-68). A fetched row replaces
				// a local one unless the local one is strictly newer, so a stale local (persisted or not) never
				// shadows a newer server row (OWA-7 #1), and a newer row that arrived while the fetch was in
				// flight (for example a socket update during startup restore) is kept.
				st = carryOver(state);
				fetched.forEach(task => applyVersion(st, task));
				dropOmitted(st);

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.TASK_FETCH_ONE_FAILURE:
			console.error('TASK_FETCH_ONE_FAILURE', action.taskId, action.err);

			// No update to storage required
			return state;

		case Actions.TASK_FETCH_FAILURE:
			console.error('TASK_FETCH_FAILURE', action.taskId, action.err);

			// No update to storage required
			return state;

		case Actions.TASK_RESTORE: {
		    let task = new Task({...action.task});
            st = state.clone();

            // One acceptance rule for every incoming version (OWA-68)
            if (!applyVersion(st, task)) return state;

            return updateStorage(st, [task]);
        }
            
		case Actions.TASK_SAVE: {
		    // console.debug('⮂ TASK_SAVE reducer', action.task?.id);
		    
			// clone the task
			let task = new Task({...action.task});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new task in the state, under the one acceptance rule (OWA-68). A rejected
			// version changes nothing and is not written to the backup.
			if (!applyVersion(st, task)) return state;

			return updateStorage(st, [task]);
		}

		case Actions.TASK_SAVE_MANY: {
		    if (logging) console.debug('⮂ TASK_SAVE_MANY reducer', action.tasks.map(task => task.id).join(','));
		    
			// clone the tasks
			let toProcess = action.tasks.map(task => new Task(task));

			st = state.clone();
			// state.clear();

			const updates: Task[] = [];

			toProcess.forEach(task => {
				// Add or update the new task in the state, under the one acceptance rule (OWA-68)
				if (applyVersion(st, task)) updates.push(task);
				else if (logging) console.debug('⮂ TASK_SAVE_MANY: rejected older version of task');
			});

			return updateStorage(st, updates);
		}

		case Actions.TASK_REMOVE: {
			// clone the task; a remove is a deleted version (the remove saga stamps it 'D' and a new
			// addedOn)
			const task = new Task({...action.task, status: 'D'});

			// Remove from UI under the one acceptance rule: the deletion version is recorded, and the row
			// is removed, or kept as a deleted row for a type that keeps them (OWA-68)
			st = state.clone();
			if (!applyVersion(st, task)) return state;

			return updateStorage(st, [task]);
		}

		case Actions.TASK_RESTORE_FAILURE:
		case Actions.TASK_SAVE_FAILURE: {
		    console.warn('TASK_SAVE_FAILURE reducer', action.task?.id);
		    if (!action.task?.id) {
				console.warn('TASK_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const task = ensure(Task, action.task);
			if (!task) {
				console.error('Could not find task after call to save');
				return state;
			}

			st = state.clone();

			const _taskRef = state.get(task.id);

			if (_taskRef && (_taskRef.addedOn === task.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _task = new Task({..._taskRef});
				_task.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_task.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.update('task', task.addedOn, {fatalError: _task.fatalError}).then(r => {
						if (r.isErr()) console.error(`⮂ taskReducer: failed to save task with fatalError info: ${r.unwrapErr().message}`);
						else if (r.unwrap() === 0) {
							// Non-document 4xx handlers may remove the row before dispatch.
							// Preserve the historical rejected-operation queue behavior.
							idb.add('task', _task).then(saved => {
								if (saved.isErr()) console.error('Could not restore rejected task: ' + saved.unwrapErr().message);
							}).catch(err => console.error('Could not restore rejected task: ' + err.message));
						}
						else console.debug('⮂ taskReducer: saved task with fatalError info');
					}).catch(err => console.error(`⮂ taskReducer: failed to save task with fatalError info: ${err.message}`));

				st.set(_task);
			}

			return updateStorage(st);
		}

		case Actions.TASK_SAVE_MANY_FAILURE: {
		    console.warn('TASK_SAVE_MANY_FAILURE reducer', action.tasks.map(task => task.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.TASK_RESTORE_SUCCESS:
		case Actions.TASK_SAVE_SUCCESS: {
			if (!action.task?.id) {
				console.warn('TASK_SAVE_SUCCESS reducer');
                return state;
			}

			const taskRef = state.get(action.task?.id);
			if (!taskRef) {
				console.warn('Could not find task after call to save');
                return state;
			}

			st = state.clone();

			// The echoed version goes through the one acceptance rule (OWA-68). When it is older than the
			// current row, only the current row's persisted flag changes, and the current row (never the
			// rejected echo) is what the backup receives.
			if (applyVersion(st, action.task)) return updateStorage(st, [action.task]);

			if (taskRef.persisted) return state;

			// Create a new instance to avoid mutating the entity shared with old state
			const task = new Task({...taskRef, persisted: true});
			st.set(task);

			return updateStorage(st, [task]);
		}

		case Actions.TASK_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ TASK_SAVE_MANY_SUCCESS reducer', action.tasks.map(task => task.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const tasks: Task[] = [];

			action.tasks.forEach(e => {
				const taskRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!taskRef) console.warn('Could not find task after call to save');
				// The echoed version goes through the one acceptance rule (OWA-68)
				else if (applyVersion(st, e)) tasks.push(e);
				else if (!taskRef.persisted) {
					// Older echo: only the current row's persisted flag changes, and the current row is
					// what the backup receives. Create a new instance to avoid mutating the entity shared
					// with old state.
					const task = new Task({...taskRef, persisted: true});
					st.set(task);
					tasks.push(task);
				}
			});

			return updateStorage(st, tasks);
		}

		case Actions.TASK_REMOVE_FAILURE: {
		    console.warn('TASK_REMOVE_FAILURE action', action.task?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.TASK_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.TASK_RESTORE_MANY: {
			if (logging) console.debug('⮂ TASK_RESTORE_MANY reducer', action.tasks.map(task => task.id).join(','));

			const toProcess = action.tasks.map(task => new Task({...task}));
			st = state.clone();

			const updates: Task[] = [];

			// One acceptance rule for every incoming version (OWA-68): a deleted version removes the row and
			// records its deletion version, or keeps a deleted row for a type that keeps them.
			toProcess.forEach(task => {
				if (applyVersion(st, task)) updates.push(task);
				else if (logging) console.debug('⮂ TASK_RESTORE_MANY: rejected older version of task');
			});

			return updateStorage(st, updates);
		}

		case Actions.TASK_REMOVE_MANY: {
			if (logging) console.debug('⮂ TASK_REMOVE_MANY reducer', action.tasks.map(task => task.id).join(','));

			// A socket delete is a deleted version, under the one acceptance rule (OWA-68)
			const toProcess = action.tasks.map(task => new Task({...task, status: 'D'}));
			st = state.clone();

			const updates: Task[] = [];

			toProcess.forEach(task => {
				if (applyVersion(st, task)) updates.push(task);
				else if (logging) console.debug('⮂ TASK_REMOVE_MANY: rejected older version of task');
			});

			return updateStorage(st, updates);
		}

		case Actions.TASK_STALE: {
			// OWA-998: a save of this tab was refused as stale. Install the record as it is now over this
			// tab's refused version, or over an older accepted version, unless a newer deletion is
			// recorded. Never over a newer row: that row stays, and View and Merge use it.
			if (!action.task) return state;
			const current = new Task({...action.task, persisted: true});
			const row = state.get(current.id);
			const deletedAt = state.deletedVersion(current.id);
			if (deletedAt !== undefined && deletedAt > current.addedOn) return state;
			if (row && row.addedOn !== action.rejectedAddedOn && !olderAccepted(row, current)) return state;

			st = state.clone();
			if (current.status === 'D') {
				st.recordDeletion(current.id, current.addedOn);
				if (row) st.delete(row);
			}
			else st.set(accepted(current));

			// The backup takes the same replacement: it may hold the refused version, stamped newer
			return updateStorage(st, [Object.assign(current, {staleReplaces: action.rejectedAddedOn})]);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default tasks;